<template>
	<section
		class="new-event-pre-post-tours"
		:class="{'new-event-pre-post-tours--standalone': !bookingEnabled}"
	>
		<wait-modal id="new-pre-post-tour-wait-modal" ref="waitModal" :title="translation.title"></wait-modal>
		<conclusion-modal id="new-pre-post-tour-conclusion-modal" ref="conclusionModal"></conclusion-modal>

		<div class="new-event-pre-post-tours__inner new-layout-container">
			<h2>{{translation.title}}</h2>

			<div class="new-event-pre-post-tours__list">
				<article
					v-for="tour in tours"
					:key="tour.id"
					class="new-event-pre-post-tours__card"
				>
					<div class="new-event-pre-post-tours__header">
						<img
							class="new-event-pre-post-tours__image"
							:src="tour.image"
							:alt="tour.title"
						>

						<h3>{{tour.title}}</h3>

						<div v-if="bookingEnabled" class="new-event-pre-post-tours__action">
							<button type="button" @click="openRequestModal(tour)">{{translation.book}}</button>
						</div>
					</div>

					<div class="new-event-pre-post-tours__body">
						<p
							v-for="(paragraph, paragraphIndex) in tour.description"
							:key="`description-${paragraphIndex}`"
							:class="{'new-event-pre-post-tours__highlight': tour.highlightDescription}"
						>
							{{paragraph}}
						</p>

						<template v-if="tour.included && tour.included.length">
							<h4>{{translation.included}}</h4>
							<ul>
								<li v-for="item in tour.included" :key="`included-${item}`">{{item}}</li>
							</ul>
						</template>

						<template v-if="tour.excluded && tour.excluded.length">
							<h4>{{translation.excluded}}</h4>
							<ul>
								<li v-for="item in tour.excluded" :key="`excluded-${item}`">{{item}}</li>
							</ul>
						</template>

						<!--
						<p v-if="tour.price" class="new-event-pre-post-tours__price">
							<span v-for="(line, priceIndex) in tour.price" :key="`price-${priceIndex}`">{{line}}</span>
						</p>
						-->

						<p v-if="tour.notes" class="new-event-pre-post-tours__notes">
							<span v-for="(note, noteIndex) in tour.notes" :key="`note-${noteIndex}`">{{note}}</span>
						</p>
					</div>
				</article>
			</div>
		</div>

		<div
			v-if="selectedTour"
			class="new-event-pre-post-tours__modal-backdrop"
			role="presentation"
			@click.self="closeRequestModal"
		>
			<section
				class="new-event-pre-post-tours__modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="new-pre-post-tour-modal-title"
			>
				<header class="new-event-pre-post-tours__modal-header">
					<div>
						<h3 id="new-pre-post-tour-modal-title">{{translation.form.title}}</h3>
						<p>{{selectedTour.title}}</p>
					</div>
					<button
						type="button"
						class="new-event-pre-post-tours__modal-close"
					:aria-label="translation.form.close"
						:disabled="isSubmitting"
						@click="closeRequestModal"
					>
						&times;
					</button>
				</header>

				<b-form class="new-event-pre-post-tours__form" @submit.prevent="sendRequest">
					<div class="new-event-pre-post-tours__field">
						<label for="new-pre-post-tour-name"><span>*</span> {{translation.form.name}}</label>
						<b-form-input
							id="new-pre-post-tour-name"
							ref="nameInput"
							v-model.trim="request.name"
							type="text"
							required
						/>
					</div>

					<div class="new-event-pre-post-tours__field">
						<label for="new-pre-post-tour-email"><span>*</span> {{translation.form.email}}</label>
						<b-form-input
							id="new-pre-post-tour-email"
							v-model.trim="request.email"
							type="email"
							required
						/>
					</div>

					<div class="new-event-pre-post-tours__field new-event-pre-post-tours__field--date">
						<label for="new-pre-post-tour-departure"><span>*</span> {{translation.form.departureDate}}</label>
						<date-picker
							id="new-pre-post-tour-departure"
							v-model="request.departureDate"
							class="new-event-pre-post-tours__date"
							:initialDefaultDate="initialDepartureDate"
							required
						/>
					</div>

					<div class="new-event-pre-post-tours__field">
						<label for="new-pre-post-tour-message">{{translation.form.message}}</label>
						<b-form-textarea
							id="new-pre-post-tour-message"
							v-model.trim="request.message"
							rows="5"
						/>
					</div>

					<footer class="new-event-pre-post-tours__modal-actions">
						<button
							type="button"
							class="new-event-pre-post-tours__modal-button new-event-pre-post-tours__modal-button--secondary"
							:disabled="isSubmitting"
							@click="closeRequestModal"
						>
							{{translation.form.close}}
						</button>
						<button
							type="submit"
							class="new-event-pre-post-tours__modal-button"
							:disabled="isSubmitting"
						>
							{{translation.form.submit}}
						</button>
					</footer>
				</b-form>
			</section>
		</div>
	</section>
</template>

<script>
import moment from 'moment';
import ConclusionModal from '../shared/ConclusionModal.vue';
import DatePicker from '../shared/DatePicker.vue';
import WaitModal from '../shared/WaitModal.vue';
import EventService from '../../services/EventService';
import { translate } from '../../assets/translations';

const COMMON_EXCLUDED_ITEMS = [
	'Passagem aérea',
	'Qualquer item não citado anteriormente',
];

const PORTUGUESE_TOURS = [
	{
		id: 5,
		title: 'BÚZIOS',
		image: '/static/pre-post-tours/buzios.jpg',
		description: [
			'Há algumas décadas, Búzios começava a se tornar um belo lugar onde as pessoas ricas e famosas iam em busca de paz e tranquilidade. Em 1960, o interesse pelo lugar aumentou com a visita da atriz francesa Brigitte Bardot, então uma famosa estrela de filmes internacionais que escolheu a cidade para relaxar durante alguns dias. Desde então, Búzios começou a fazer parte do itinerário do turismo internacional. Búzios tem uma boa infraestrutura de hotéis e hospedagens de categoria internacional, restaurantes e bares agradáveis. Atualmente, a Península ainda mantém um pouco da aldeia pesqueira que sempre deu um toque especial à cidade.',
		],
		included: [
			'2 noites na Pousada Pedra da Laguna',
			'Traslados Aeroporto Rio de Janeiro x Búzios',
			'Passeio de lancha (6 horas)',
		],
		excluded: COMMON_EXCLUDED_ITEMS,
		price: [
			'A partir de 6x de R$310,00 por pessoa em apartamento duplo',
			'Total R$1.860,00 por pessoa',
		],
		notes: ['Preço sob consulta']
	},
	{
		id: 2,
		title: 'FOZ DO IGUAÇU',
		image: '/static/pre-post-tours/foz-do-iguacu.jpg',
		description: [
			'Foz do Iguaçu é privilegiada por estar localizada na tríplice fronteira do Brasil com a Argentina e o Paraguai, e é conhecida como a “Cidade das Quedas”, onde os rios Paraná e Iguaçu formam um dos mais belos cenários naturais: as Cataratas do Iguaçu, visitadas por pessoas dos mais diversos cantos do planeta.',
			'A cidade também dispõe de atrativos imperdíveis, com destaque para a arquitetura religiosa, como a Igreja Matriz, o Templo Budista, a Mesquita Muçulmana e a Hidrelétrica de Itaipu, a maior do mundo em produção de energia.',
		],
		included: [
			'3 noites no Hotel Grand Carimã',
			'Traslados Aeroporto de Foz x Hotel',
			'Cataratas Brasileiras',
			'Macuco Safari',
			'Parque das Aves',
		],
		excluded: COMMON_EXCLUDED_ITEMS,
		price: [
			'A partir de 6x de R$445,00 por pessoa em apartamento duplo',
			'Total R$2.670,00 por pessoa',
		],
		notes: ['Preço sob consulta']
	},
	{
		id: 6,
		title: 'PARATY',
		image: '/static/pre-post-tours/paraty.jpg',
		description: [
			'Paraty é uma charmosa cidade colonial, reconhecida como Patrimônio Histórico e Cultural, preservando até hoje sua arquitetura impecável e um cenário natural encantador. Caminhar pelo seu Centro Histórico livre de carros é como viajar no tempo, descobrindo ruas de pedra, casarões seculares e histórias do ciclo do ouro e do café — períodos em que Paraty teve grande importância econômica e portuária.',
			'O pacote inclui 3 noites no Sandi Hotel com café da manhã, traslados entre o Aeroporto do Rio de Janeiro e Paraty, além de experiências completas: um passeio de escuna Netuno Vintage pelas águas calmas da baía e um City Tour com degustação de cachaça, que revela a cultura e os sabores locais.',
		],
		included: [
			'3 noites no Sandi Hotel com café da manhã',
			'Traslados Aeroporto Rio de Janeiro x Paraty',
			'Passeio de Escuna Netuno Vintage',
			'City Tour com Degustação de Cachaça',
		],
		excluded: COMMON_EXCLUDED_ITEMS,
		price: [
			'A partir de 6x de R$556,16 por pessoa em apartamento duplo',
			'Total R$3.337,00 por pessoa',
		],
		notes: ['Preço sob consulta']
	},
	{
		id: 73,
		title: 'ANAVILHANAS ECO LODGE - AMAZONAS',
		image: '/static/pre-post-tours/anavilhanas-eco-lodge.jpg',
		description: [
			'Viva uma experiência inesquecível na Amazônia! Aproveite 4 dias e 3 noites no exclusivo Anavilhanas Lodge, com refeições, traslados e todas as atividades do hotel inclusas.',
			'Não inclui passagem aérea nem itens não mencionados.',
		],
		price: [
			'💰 Valor por pessoa: R$10.220,00',
			'ou em 6x de R$1.703,33',
		],
		notes: ['Preço sob consulta']
	},
	{
		id: 72,
		title: 'LENÇOIS MARANHENSES - BARREIRINHAS',
		image: '/static/pre-post-tours/lencois-maranhenses.jpg',
		description: [
			'Descubra Barreirinhas, a porta de entrada para os incríveis Lençóis Maranhenses. Hospedando-se por 4 noites na charmosa Pousada Encantes do Nordeste, com café da manhã incluso, você vivencia conforto e tranquilidade às margens do Rio Preguiças. O pacote oferece transporte privativo entre o aeroporto de São Luís e Barreirinhas, garantindo comodidade desde a chegada.',
			'Durante a viagem, você explora o melhor da região com passeios completos: o Circuito Lagoa Bonita, famoso por dunas impressionantes e pôr do sol inesquecível; o Circuito Lagoas do Atins (Canto do Atins), com lagoas cristalinas e culinária típica; e o Circuito Santo Amaro (Betânia), uma das áreas mais preservadas e tranquilas do parque. Ao final, o retorno a São Luís também é feito em transporte privativo.',
		],
		included: [
			'04 noites em Barreirinhas na Pousada Encantes do Nordeste com café da manhã',
			'Transporte privativo Aeroporto de São Luís / Barreirinhas',
			'Circuito Lagoa Bonita',
			'Circuito Lagoas do Atins (Canto do Atins)',
			'Circuito Santo Amaro (Betânia)',
			'Transporte privativo Barreirinhas / São Luís',
		],
		excluded: COMMON_EXCLUDED_ITEMS,
		price: [
			'A partir de 6x de R$791,00 por pessoa em apartamento duplo',
			'Total de R$4.746,00 por pessoa',
		],
		notes: ['Preço sob consulta']
	},
	{
		id: 68,
		title: 'Pacote Personalizado: Seu Pacote Ideal',
		image: '/static/pre-post-tours/pacote-personalizado.jpg',
		description: [
			'Quer um roteiro exclusivo para explorar a cidade? Oferecemos um pacote personalizado com as melhores opções turísticas. Planeje sua viagem com detalhes sob medida e descubra as atrações mais encantadoras com todo o conforto e conveniência.',
		],
		highlightDescription: true,
	}
];

export default {
	components: {
		ConclusionModal,
		DatePicker,
		WaitModal,
	},
	props: {
		bookingEnabled: {
			type: Boolean,
			default: true,
		},
		event: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			eventService: new EventService(),
			isSubmitting: false,
			request: this.emptyRequest(),
			selectedTour: null,
		};
	},
	computed: {
		translation() {
			return translate(this.$store.state.language).prePostTours;
		},
		tours() {
			const localizedTours = this.translation.tours || {};
			return PORTUGUESE_TOURS.map(tour => ({
				...tour,
				...(localizedTours[tour.id] || {}),
			}));
		},
		initialDepartureDate() {
			return this.event && this.event.endDate
				? moment(this.event.endDate).format('DD/MM/YYYY')
				: moment().format('DD/MM/YYYY');
		},
	},
	mounted() {
		document.addEventListener('keyup', this.closeOnEscape);
	},
	beforeDestroy() {
		document.body.style.overflow = '';
		document.removeEventListener('keyup', this.closeOnEscape);
	},
	methods: {
		closeOnEscape(event) {
			if (event.key === 'Escape') this.closeRequestModal();
		},
		closeRequestModal() {
			if (this.isSubmitting) return;
			this.selectedTour = null;
			document.body.style.overflow = '';
		},
		emptyRequest() {
			return {
				departureDate: '',
				email: '',
				message: '',
				name: '',
			};
		},
		openRequestModal(tour) {
			this.request = this.emptyRequest();
			this.selectedTour = tour;
			document.body.style.overflow = 'hidden';
			this.$nextTick(() => {
				if (this.$refs.nameInput && this.$refs.nameInput.focus) this.$refs.nameInput.focus();
			});
		},
		formatDepartureDate(date) {
			if (!date) return '';

			const dateText = String(date);
			if (moment(dateText, 'DD/MM/YYYY', true).isValid()) return dateText;

			const dateOnly = dateText.split('T')[0];
			const parsedDate = moment(dateOnly, 'YYYY-MM-DD', true);
			return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : dateText;
		},
		requestPayload() {
			return {
				prePostTourId: this.selectedTour.id,
				prePostTourName: this.selectedTour.title,
				name: this.request.name,
				email: this.request.email,
				departureDate: this.formatDepartureDate(this.request.departureDate),
				message: this.request.message,
			};
		},
		async sendRequest() {
			if (this.isSubmitting || !this.event || !this.event.id || !this.selectedTour) return;

			this.isSubmitting = true;
			if (this.$refs.waitModal) this.$refs.waitModal.open(this.translation.form.sending);

			try {
				await this.eventService.sendPrePostTourRequest(this.event.id, this.requestPayload());
				this.selectedTour = null;
				document.body.style.overflow = '';
				this.$refs.conclusionModal.openMessage(this.translation.form.success);
			} catch (error) {
				this.$refs.conclusionModal.open(
					false,
					this.translation.form.failureTitle,
					this.translation.form.failureMessage,
				);
			} finally {
				this.isSubmitting = false;
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-pre-post-tours {
	background: #ffffff;
	color: #4c4b4b;
}

.new-event-pre-post-tours__inner {
	padding-bottom: 8%;
}

.new-event-pre-post-tours--standalone .new-event-pre-post-tours__inner {
	padding-top: 4%;
}

.new-event-pre-post-tours__inner > h2 {
	margin: 0 0 3%;
	color: #002c41;
	font-size: 22px;
	font-weight: 900;
	line-height: 26px;
}

.new-event-pre-post-tours__list {
	display: flex;
	flex-direction: column;
	gap: 34px;
}

.new-event-pre-post-tours__card {
	box-sizing: border-box;
	width: 100%;
	overflow: hidden;
	padding: 2.5rem 1.2% 2rem;
	border: 1px solid #d7d7d7;
	border-radius: 1.8rem;
	background: #ffffff;
}

.new-event-pre-post-tours__header {
	display: flex;
	align-items: flex-start;
	gap: 3.5%;
	padding: 0 2.5% 0.8rem;
}

.new-event-pre-post-tours__image {
	display: block;
	flex: 0 1 21%;
	width: 21%;
	min-width: 280px;
	max-width: 348px;
	aspect-ratio: 348 / 232;
	border-radius: 1.5rem;
	object-fit: cover;
}

.new-event-pre-post-tours__header h3 {
	flex: 1 1 40%;
	margin: 2.4rem 0 0;
	color: #002c41;
	font-size: 25px;
	font-weight: 700;
	line-height: 29px;
}

.new-event-pre-post-tours__action {
	display: flex;
	flex: 0 1 15%;
	align-self: flex-end;
	justify-content: flex-end;
	min-width: 200px;
	padding-bottom: 0.3rem;
}

.new-event-pre-post-tours__action button {
	box-sizing: border-box;
	width: 100%;
	max-width: 242px;
	height: 36px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	color: #ffffff;
	cursor: pointer;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
}

.new-event-pre-post-tours__body {
	padding: 2rem 2.5% 0;
	border-top: 1px solid #d7d7d7;
	font-size: 18px;
	font-weight: 400;
	line-height: 1.65;
}

.new-event-pre-post-tours__body p {
	margin: 0 0 1rem;
}

.new-event-pre-post-tours__body h4 {
	margin: 1.4rem 0 0.4rem;
	font-size: inherit;
	font-weight: 700;
	line-height: inherit;
}

.new-event-pre-post-tours__body ul {
	margin: 0 0 1rem;
	padding-left: 1.5rem;
}

.new-event-pre-post-tours__body li {
	margin-bottom: 0.2rem;
}

.new-event-pre-post-tours__highlight {
	font-weight: 700;
}

.new-event-pre-post-tours__price span,
.new-event-pre-post-tours__notes span {
	display: block;
}

.new-event-pre-post-tours__notes {
	font-style: italic;
}

.new-event-pre-post-tours__modal-backdrop {
	position: fixed;
	inset: 0;
	z-index: 1050;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	overflow-y: auto;
	padding: 5vh 5%;
	background: rgba(0, 0, 0, .55);
}

.new-event-pre-post-tours__modal {
	box-sizing: border-box;
	width: 100%;
	max-width: 680px;
	margin: auto;
	overflow: hidden;
	border-radius: 13px;
	background: #ffffff;
	box-shadow: 0 18px 50px rgba(4, 30, 45, .24);
}

.new-event-pre-post-tours__modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	min-height: 53px;
	padding: 12px 11px 12px 20px;
	background: #67adc6;
	color: #ffffff;
}

.new-event-pre-post-tours__modal-header h3 {
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	line-height: 20px;
}

.new-event-pre-post-tours__modal-header p {
	margin: 6px 0 0;
	font-size: 14px;
	font-weight: 500;
	line-height: 19px;
	text-transform: uppercase;
}

.new-event-pre-post-tours__modal-close {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 34px;
	width: 34px;
	height: 34px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #ffffff;
	font-size: 26px;
	font-weight: 700;
	line-height: 1;
	cursor: pointer;
}

.new-event-pre-post-tours__modal-close:focus-visible {
	outline: 2px solid #ffffff;
	outline-offset: -4px;
}

.new-event-pre-post-tours__form {
	padding: 30px 32px 26px;
}

.new-event-pre-post-tours__field {
	margin-bottom: 20px;
}

.new-event-pre-post-tours__field--date {
	max-width: 260px;
}

.new-event-pre-post-tours__field label {
	display: block;
	margin-bottom: 7px;
	color: #002c41;
	font-size: 15px;
	font-weight: 700;
	line-height: 19px;
}

.new-event-pre-post-tours__field label span {
	color: #e89127;
}

.new-event-pre-post-tours__field .form-control {
	box-sizing: border-box;
	width: 100%;
	height: 42px;
	padding: 8px 12px;
	border: 1px solid #c9c9c9;
	border-radius: 8px;
	background: #ffffff;
	color: #4c4b4b;
	font-family: inherit;
	font-size: 15px;
	line-height: 20px;
}

.new-event-pre-post-tours__field textarea.form-control {
	height: 130px;
	min-height: 100px;
	resize: vertical;
}

.new-event-pre-post-tours__field .form-control:focus,
.new-event-pre-post-tours__date:focus-within {
	border-color: #5da3bd;
	box-shadow: 0 0 0 3px rgba(93, 163, 189, .15);
	outline: none;
}

.new-event-pre-post-tours__date {
	border-radius: 8px;
}

.new-event-pre-post-tours__date >>> .datetime-input {
	box-sizing: border-box;
	width: 100%;
	height: 42px !important;
	padding: 8px 44px 8px 12px;
	border: 1px solid #c9c9c9;
	border-radius: 8px;
	background: #ffffff;
	color: #4c4b4b;
	font-family: inherit;
	font-size: 15px;
	line-height: 20px;
}

.new-event-pre-post-tours__date >>> .calendar-icon {
	bottom: 5px;
	right: 5px;
	width: 31px;
}

.new-event-pre-post-tours__modal-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 26px;
	padding-top: 22px;
	border-top: 1px solid #d7d7d7;
}

.new-event-pre-post-tours__modal-button {
	box-sizing: border-box;
	min-width: 180px;
	height: 40px;
	padding: 0 22px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	cursor: pointer;
	font-family: inherit;
	font-size: 15px;
	font-weight: 700;
	line-height: 19px;
}

.new-event-pre-post-tours__modal-button--secondary {
	min-width: 110px;
	border-color: #5da3bd;
	background: #ffffff;
	color: #376f85;
}

.new-event-pre-post-tours__modal-button:disabled,
.new-event-pre-post-tours__modal-close:disabled {
	cursor: wait;
	opacity: .65;
}

@media (max-width: 1100px) {
	.new-event-pre-post-tours__header {
		flex-wrap: wrap;
	}

	.new-event-pre-post-tours__image {
		flex-basis: 30%;
		width: 30%;
	}

	.new-event-pre-post-tours__header h3 {
		flex-basis: 50%;
	}

	.new-event-pre-post-tours__action {
		flex-basis: 100%;
	}
}

@media (max-width: 900px) {
	.new-event-pre-post-tours__inner {
		padding-bottom: 18%;
	}

	.new-event-pre-post-tours__inner > h2 {
		margin-bottom: 7%;
		font-size: 20px;
		line-height: 24px;
	}

	.new-event-pre-post-tours__list {
		gap: 24px;
	}

	.new-event-pre-post-tours__card {
		padding: 1.5rem 4% 1.4rem;
		border-radius: 1.5rem;
	}

	.new-event-pre-post-tours__header {
		flex-direction: column;
		align-items: stretch;
		gap: 1.4rem;
		padding: 0 0 1.5rem;
	}

	.new-event-pre-post-tours__image {
		flex-basis: auto;
		width: 100%;
		min-width: 0;
		max-width: none;
		border-radius: 1.1rem;
	}

	.new-event-pre-post-tours__header h3 {
		margin: 0;
		font-size: 21px;
		line-height: 26px;
	}

	.new-event-pre-post-tours__action {
		flex-basis: auto;
		align-self: stretch;
		justify-content: flex-start;
		min-width: 0;
		padding: 0;
	}

	.new-event-pre-post-tours__action button {
		max-width: 220px;
	}

	.new-event-pre-post-tours__body {
		padding: 1.5rem 0 0;
		font-size: 16px;
		line-height: 1.6;
	}
}

@media (max-width: 480px) {
	.new-event-pre-post-tours__action button {
		max-width: none;
	}

	.new-event-pre-post-tours__modal-backdrop {
		align-items: flex-start;
		padding: 4%;
	}

	.new-event-pre-post-tours__modal {
		border-radius: 13px;
	}

	.new-event-pre-post-tours__modal-header,
	.new-event-pre-post-tours__form {
		padding-right: 20px;
		padding-left: 20px;
	}

	.new-event-pre-post-tours__modal-header h3 {
		font-size: 20px;
		line-height: 24px;
	}

	.new-event-pre-post-tours__field--date {
		max-width: none;
	}

	.new-event-pre-post-tours__modal-actions {
		flex-direction: column-reverse;
		align-items: stretch;
	}

	.new-event-pre-post-tours__modal-button,
	.new-event-pre-post-tours__modal-button--secondary {
		width: 100%;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/local-tour/PrePostTours.vue